import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Placeholder, SitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Route, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  GlobalCSS,
  CurrentBreakpointProvider,
  MediaContextProvider,
  OptionalVideoModalContextProvider,
  OptionalVideoModal,
} from '@honda-canada/design-system-react';

import RouteHandler from './RouteHandler';
import RouteHandlerHeader from './RouteHandlerHeader';
import gtmDataLayerHandler from '../utils/gtmDataLayerHandler';
import getInitialState from '../utils/getInitialState';
import GtmGlobal from './GtmGlobal';

// This is the main JSX entry point of the app invoked by the renderer (server or client rendering).

// support languages in the URL prefix
// e.g. /en/path, or /path
export const routePatterns = ['/:lang(en|fr)/:sitecoreRoute*', '/:sitecoreRoute*'];

const AppRoot = ({
  activePlaceholders = ['header', 'main', 'footer'],
  componentFactory,
  config,
  bottomElementProvider,
  // eslint-disable-next-line no-console
  errorHandler = error => console.error(error),
  errorMessage,
  isUserLocationModalOpen,
  mainContainer,
  mainPlaceholderName = 'main',
  modelExplorationContextProvider,
  modelFiltersContextProvider,
  popupContextProvider,
  path,
  Router: RouterProvider,
  ReducersProvider,
  setIsUserLocationModalOpen,
  ssrState,
  theme,
  updateExternalLanguageLinks,
  userLocationModal,
}) => {
  const sitecoreContext = getInitialState({ state: ssrState, config });

  const [ssrRenderComplete, setSsrRenderComplete] = useState(false);

  useEffect(() => {
    setSsrRenderComplete(true);
  }, []);

  const MainContainer = mainContainer || React.Fragment;
  const ModelExplorationContextProvider = modelExplorationContextProvider || React.Fragment;
  const PopupContextProvider = popupContextProvider || React.Fragment;
  const ModelFiltersContextProvider = modelFiltersContextProvider || React.Fragment;
  const UserLocationModal = userLocationModal || React.Fragment;
  const BottomElementProvider = bottomElementProvider || React.Fragment;

  const headerPlaceholderName = activePlaceholders.find(name => name === 'acura-header') || 'header';
  const footerPlaceholderName = activePlaceholders.find(name => name === 'acura-footer') || 'footer';

  return (
    <SitecoreContext componentFactory={componentFactory} context={sitecoreContext}>
      <ThemeProvider theme={theme}>
        <RouterProvider location={path} context={{}}>
          <GtmGlobal
            route={sitecoreContext?.route}
            siteName={sitecoreContext?.site?.name}
            mainPlaceholderName={mainPlaceholderName}
            errorHandler={errorHandler}
          />
          <ReducersProvider />
          <OptionalVideoModalContextProvider>
            <CurrentBreakpointProvider>
              <GlobalCSS />
              <MediaContextProvider>
                <BottomElementProvider>
                  <Switch>
                    {routePatterns.map(routePattern => (
                      <Route
                        key={routePattern}
                        path={routePattern}
                        render={props => (
                          <RouteHandlerHeader
                            route={props}
                            config={config}
                            ssrRenderComplete={ssrRenderComplete}
                            placeholderName={headerPlaceholderName}
                            errorMessage={errorMessage}
                            updateExternalLanguageLinks={updateExternalLanguageLinks}
                            onRouteChange={() => null}
                          />
                        )}
                      />
                    ))}
                  </Switch>
                  {activePlaceholders.includes(mainPlaceholderName) && (
                    <MainContainer>
                      <ModelFiltersContextProvider>
                        <ModelExplorationContextProvider>
                          <PopupContextProvider>
                            <Switch>
                              {routePatterns.map(routePattern => (
                                <Route
                                  key={routePattern}
                                  path={routePattern}
                                  render={props => (
                                    <RouteHandler
                                      route={props}
                                      config={config}
                                      ssrRenderComplete={ssrRenderComplete}
                                      placeholderName={mainPlaceholderName}
                                      errorMessage={errorMessage}
                                      updateExternalLanguageLinks={updateExternalLanguageLinks}
                                      onRouteChange={newRoute =>
                                        gtmDataLayerHandler(
                                          newRoute,
                                          sitecoreContext?.site?.name,
                                          mainPlaceholderName,
                                          errorHandler,
                                        )
                                      }
                                    />
                                  )}
                                />
                              ))}
                            </Switch>
                          </PopupContextProvider>
                        </ModelExplorationContextProvider>
                      </ModelFiltersContextProvider>
                    </MainContainer>
                  )}
                  {footerPlaceholderName && sitecoreContext?.route ? (
                    <Placeholder name={footerPlaceholderName} rendering={sitecoreContext?.route} />
                  ) : (
                    <UserLocationModal
                      allowClose
                      isOpen={isUserLocationModalOpen}
                      setIsUserLocationModalOpen={setIsUserLocationModalOpen}
                      onLocationDetected={() => setIsUserLocationModalOpen(false)}
                    />
                  )}
                </BottomElementProvider>
              </MediaContextProvider>
            </CurrentBreakpointProvider>
            <OptionalVideoModal />
          </OptionalVideoModalContextProvider>
        </RouterProvider>
      </ThemeProvider>
    </SitecoreContext>
  );
};

AppRoot.propTypes = {
  ssrState: PropTypes.shape({
    sitecore: PropTypes.shape({
      route: PropTypes.shape({
        itemId: PropTypes.string,
        name: PropTypes.string,
        path: PropTypes.string,
        templateName: PropTypes.string,
      }),
      context: PropTypes.shape({
        site: PropTypes.shape({
          name: PropTypes.string,
          hostname: PropTypes.string,
        }),
        pageEditing: PropTypes.bool,
        language: PropTypes.string,
        dictionary: PropTypes.shape({}),
        provinces: PropTypes.arrayOf(PropTypes.shape({})),
        settings: PropTypes.shape({}),
        sharedApps: PropTypes.shape({}),
        visitorIdentificationTimestamp: PropTypes.string,
      }),
    }),
  }).isRequired,
  errorHandler: PropTypes.func,
  path: PropTypes.string.isRequired,
  ReducersProvider: PropTypes.elementType.isRequired,
  Router: PropTypes.elementType.isRequired,
  config: PropTypes.shape({
    hondaRestApiHost: PropTypes.string,
    sotHondaExpApiConsumerName: PropTypes.string,
    sotHondaExpApiConsumerId: PropTypes.string,
    sotHondaExpApiEnv: PropTypes.string,
    sitecoreApiHost: PropTypes.string,
    sitecoreApiKey: PropTypes.string,
    layoutServiceRoute: PropTypes.string,
  }).isRequired,
  errorMessage: PropTypes.string.isRequired,
  updateExternalLanguageLinks: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    colors: PropTypes.shape({
      primary: PropTypes.string,
      secondary: PropTypes.string,
      tertiary: PropTypes.string,
      accent: PropTypes.string,
      font: PropTypes.shape({
        color: PropTypes.string,
      }),
    }),
  }).isRequired,
  mainPlaceholderName: PropTypes.string,
  activePlaceholders: PropTypes.arrayOf(PropTypes.string),
  mainContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.element]),
  modelExplorationContextProvider: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.element]),
  popupContextProvider: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.element]),
  modelFiltersContextProvider: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.element]),
  componentFactory: PropTypes.func.isRequired,
  setIsUserLocationModalOpen: PropTypes.func.isRequired,
  isUserLocationModalOpen: PropTypes.bool.isRequired,
  userLocationModal: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.element]),
  bottomElementProvider: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.element]),
};

export default AppRoot;
